import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService as Auth0Service, User } from '@auth0/auth0-angular';
import { BehaviorSubject, map, Observable, take } from 'rxjs';
import { environment } from '../../../environments/environment';
import { EStorageKeys } from '../enums/storageKeys';
import { USER_ROLE } from '../enums/user-role.enum';
import { HelperService } from './helper.service';
import { ToastService } from './toast.service';

interface AuthResponse {
  access_token: string;
  refresh_token: string;
  id_token: string;
  expires_in: number;
  token_type: string;
}

interface LoginError {
  error: string;
  error_description: string;
  message?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public initializationComplete = new BehaviorSubject<Boolean | null>(null);

  constructor(private auth0: Auth0Service, private http: HttpClient, private toastService: ToastService, private helperService: HelperService) {
    this.loadUserOnceOnStartup();
  }

  /**
   * Extracts the user role from Auth0 Claims
   * @param claims Auth0 ID Token Claims
   * @returns The first role or null if none available
   */
  private extractUserRole(claims: any): string | null {
    if (claims && claims[environment.roleNamespace]) {
      const roles = claims[environment.roleNamespace];
      if (Array.isArray(roles) && roles.length > 0) {
        return roles[0];
      }
    }
    return null;
  }

  /**
   * Extracts the user role directly from the Auth0 User Object
   * @param user Auth0 User Object
   * @returns The first role or null if none available
   */
  private extractUserRoleFromUser(user: User): string | null {
    if (user && user[environment.roleNamespace]) {
      const roles = user[environment.roleNamespace];
      if (Array.isArray(roles) && roles.length > 0) {
        return roles[0];
      }
    }
    return null;
  }

  /**
   * Loads user data based on the role
   * @param role The user role
   */
  private loadUserDataByRole(role: string | null): void {
    switch (role) {
      case USER_ROLE.GYM:
        this.getGymByAuth0Id();
        break;
      case USER_ROLE.ADMIN:
        this.getUserDataByAuth0Id();
        break;
      default:
        this.initializationComplete.next(true);
        break;
    }
  }

  // Subscribe to Auth0's user updates (better approach than idTokenClaims$)
  public loadUserOnceOnStartup(): void {
    this.auth0.user$.pipe(take(1)).subscribe(user => {
      if (user) {
        // Store user data in localStorage
        localStorage.setItem(EStorageKeys.AUTH0_USER, JSON.stringify(user));

        // Extract role directly from User object
        const userRole = this.extractUserRoleFromUser(user);
        this.loadUserDataByRole(userRole);
      } else {
        // No user available - complete initialization
        this.initializationComplete.next(true);
      }
    });
  }

  public getGymByAuth0Id() {
    // First get the Auth0 ID from the user
    this.getAuth0Id()
      .pipe(take(1))
      .subscribe(auth0Id => {
        if (!auth0Id) {
          this.initializationComplete.next(true);
          return;
        }

        // Use the Auth0 ID to fetch the gym data from the backend
        this.http
          .get(`${environment.apiUrl}/gym/${auth0Id}/auth0`)
          .pipe(take(1))
          .subscribe({
            next: (response: any) => {
              const gym = response.DATA.gym;
              localStorage.setItem(EStorageKeys.CURRENT_USER, JSON.stringify(gym));

              this.initializationComplete.next(true);
            },
            error: (error: HttpErrorResponse) => {
              const errorMessage = 'Fehler beim Laden des Gyms';
              this.helperService.captureError(error, 'frontend-auth-service', 'getGymByAuth0Id', errorMessage, undefined, 'warning');
              this.toastService.showError(errorMessage);
              this.initializationComplete.next(true);
            },
          });
      });
  }

  public getUserDataByAuth0Id() {
    // First get the Auth0 ID from the user
    this.getAuth0Id()
      .pipe(take(1))
      .subscribe(auth0Id => {
        if (!auth0Id) {
          this.initializationComplete.next(true);
          return;
        }

        // Use the Auth0 ID to fetch the user data from the backend
        this.http
          .get(`${environment.apiUrl}/user/${encodeURIComponent(auth0Id)}`)
          .pipe(take(1))
          .subscribe({
            next: (response: any) => {
              const user = response.DATA;
              localStorage.setItem(EStorageKeys.CURRENT_USER, JSON.stringify(user));

              this.initializationComplete.next(true);
            },
            error: (error: HttpErrorResponse) => {
              const errorMessage = 'Fehler beim Laden des Users';
              this.helperService.captureError(error, 'frontend-auth-service', 'getUserDataByAuth0Id', errorMessage, undefined, 'warning');
              this.toastService.showError(errorMessage);
              this.initializationComplete.next(true);
            },
          });
      });
  }

  public login(): Observable<void> {
    return this.auth0.loginWithRedirect({
      appState: {
        redirectUrl: '/login',
      },
    });
  }

  public isAuthenticated(): Observable<boolean> {
    return this.auth0.isAuthenticated$;
  }

  public getUser(): Observable<any> {
    return this.auth0.user$;
  }

  /**
   * Get user role synchronously from localStorage
   * @returns The current user role as a string, or null if not available
   */
  public getUserRoleFromLocalStorage(): string | null {
    const user = localStorage.getItem(EStorageKeys.AUTH0_USER);
    if (user) {
      try {
        const claims = JSON.parse(user);
        return this.extractUserRole(claims);
      } catch (error) {
        this.helperService.captureError(
          error,
          'auth',
          'get-user-role-from-localStorage',
          'Failed to get user role from localStorage'
        );
        return null;
      }
    }
    return null;
  }

  public getCurrentUserFromLocalStorage(): any {
    const currentUser = JSON.parse(localStorage.getItem(EStorageKeys.CURRENT_USER) || '{}');
    return currentUser;
  }

  public setCurrentUserToLocalStorage(user: any): void {
    localStorage.setItem(EStorageKeys.CURRENT_USER, JSON.stringify(user));
  }

  public getAuth0Id(): Observable<string | null> {
    return this.auth0.user$.pipe(
      map(user => {
        if (!user) return null;
        return user['sub'] || null;
      })
    );
  }

  public getCurrentUser(): any {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }

  public logout(): void {
    localStorage.clear();
    this.auth0.logout({
      logoutParams: {
        returnTo: `${window.location.origin}/login`,
      },
    });
  }
}
